"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  FileSpreadsheet,
  HandCoins,
  Settings,
} from "lucide-react";
import { BahtIcon } from "@/app/components/icons/BahtIcon";
import {
  AERA_BUSINESS_OPTIONS,
  FlowPickerSheet,
  getFlowPickerPresentation,
  type AeraBusinessOption,
  type AeraBusinessType,
} from "@/app/components/AeraBusinessOptionsGrid";
import { ModalShell } from "@/app/components/ui/ModalShell";
import { ModalCloseButton } from "@/app/components/ui/ModalParts";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { showToast } from "@/app/components/ui/Toast";
import { formatThaiDatetime, type Transaction } from "@aera/data-table";
import type { Workspace } from "@/lib/api";
import { useGoogleIntegrationStatus } from "@/hooks/useGoogleIntegrationStatus";
import { useWorkspaceReceivableSummary } from "@/hooks/useWorkspaceReceivableSummary";
import { useWorkspaceRecentTransactions } from "@/hooks/useWorkspaceRecentTransactions";
import { useWorkspaceTransactionSummary } from "@/hooks/useWorkspaceTransactionSummary";
import {
  formatGoogleSheetsLastActivity,
  getGoogleConnectionPresentation,
  getGoogleConnectionPresentationStyle,
  getGoogleSheetUrl,
  resolveGoogleConnectionTimestamp,
  type GoogleIntegrationStatus,
} from "@/lib/googleIntegrationStatus";
import {
  DEFAULT_TRANSACTION_WORKER_ID,
  EXPENSE_TRANSACTION_WORKER_ID,
  formatCombinedTransactionNetAmountLabel,
  formatLatestTransactionAmountLabel,
  RECENT_WORKSPACE_TRANSACTION_LIMIT,
  type CombinedTransactionSummary,
} from "@/lib/transactionSummary";
import { BILLIN_RECEIVABLE_WORKER_ID } from "@/lib/billinConstants";
import {
  BILLIN_SCROLL_TO_GOOGLE_SHEETS,
  BILLIN_SCROLL_TO_RECEIVABLE,
  BILLIN_SCROLL_TO_TRANSACTIONS,
  buildBillinDestination,
} from "@/lib/billinGoRoutes";
import {
  resolveWorkspaceBusinessType,
  resolveWorkspaceSalesConfigLabel,
  saveWorkspaceBusinessTypeConfig,
} from "@/lib/workspaceSalesConfig";
import { useWorkspaceShopPins } from "@/hooks/useWorkspaceShopPins";
import {
  DEFAULT_ACTIVE_SHOP_PINS,
  saveWorkspaceShopPinsConfig,
  type ShopPinId,
} from "@/lib/workspaceShopPins";

function openGoogleSheetsFromShop({
  router,
  workspaceId,
  status,
}: {
  router: ReturnType<typeof useRouter>;
  workspaceId?: string | null;
  status: GoogleIntegrationStatus | null;
}) {
  const id = workspaceId?.trim();
  const sheetUrl = getGoogleSheetUrl(status, id);

  if (sheetUrl) {
    window.open(sheetUrl, "_blank", "noopener,noreferrer");
    return;
  }

  if (!id) {
    showToast("ไม่พบเวิร์กสเปซที่จะเชื่อมต่อ Google");
    return;
  }

  router.push(
    buildBillinDestination("app", id, {
      scrollTo: BILLIN_SCROLL_TO_GOOGLE_SHEETS,
    }),
  );
}

function getTransactionRecordedAtIso(
  transaction: Transaction | null,
): string | null {
  if (!transaction) return null;

  const extended = transaction as Transaction & {
    record_date?: unknown;
    created_at?: unknown;
  };
  const raw =
    transaction.recorded_at ?? extended.record_date ?? extended.created_at;
  if (raw == null) return null;

  if (typeof raw === "object" && raw !== null && "value" in raw) {
    const value = (raw as { value: unknown }).value;
    return value == null ? null : String(value);
  }

  return String(raw);
}

function getTransactionRecordId(transaction: Transaction): string {
  const raw = transaction.record_id as unknown;
  if (raw && typeof raw === "object" && "value" in raw) {
    const value = (raw as { value?: unknown }).value;
    return value == null ? "" : String(value);
  }
  return raw == null ? "" : String(raw);
}

function formatActivityAmountLabel(
  transaction: Transaction | null,
  isExpense: boolean,
): string | null {
  const raw = formatLatestTransactionAmountLabel(transaction, isExpense);
  if (raw === "—") return null;
  return raw.replace(/\s+THB$/, "");
}

type ActivityFeedItemProps = {
  icon: React.ReactNode;
  iconClassName: string;
  title: string;
  subtitle: string;
  trailing?: React.ReactNode;
  onClick?: () => void;
  isLast?: boolean;
};

function ActivityFeedItem({
  icon,
  iconClassName,
  title,
  subtitle,
  trailing,
  onClick,
  isLast = false,
}: ActivityFeedItemProps) {
  return (
    <AeraButton
      type="button"
      variant="transparent"
      paddingSize="none"
      onClick={onClick}
      className={[
        "w-full items-center! justify-between! gap-2.5 rounded-none border-0 px-3 py-2.5 text-left transition-colors hover:bg-aera-offwhite!",
        !isLast ? "border-b border-aera-lightgray" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconClassName}`}
        >
          {icon}
        </span>
        <div className="min-w-0">
          <div className="text-[12.5px] font-bold text-aera-charcoal">
            {title}
          </div>
          <div className="mt-0.5 text-[10.5px] text-aera-gray">{subtitle}</div>
        </div>
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </AeraButton>
  );
}

export type PinId = ShopPinId;

export type WorkerShopPinDetailBridge = {
  openPinDetail: (id: PinId) => void;
};

type ActivePins = Record<PinId, boolean>;

const DEFAULT_ACTIVE_PINS: ActivePins = DEFAULT_ACTIVE_SHOP_PINS;

type PinPresentation = {
  id: PinId;
  label: string;
  icon: React.ReactNode;
  iconClassName: string;
};

function buildPinPresentations(
  businessType: AeraBusinessType | null | undefined,
): PinPresentation[] {
  const { Icon, iconBg, iconColor } = getFlowPickerPresentation(
    businessType ?? null,
  );

  return [
    {
      id: "transactions",
      label: "รายการเงินเข้า/ออก",
      icon: <BahtIcon className="h-5 w-5 text-aera-green" />,
      iconClassName: "bg-aera-greenlight text-aera-green",
    },
    {
      id: "receivable",
      label: "ยอดค้าง",
      icon: (
        <HandCoins
          className="h-5 w-5 text-aera-red"
          strokeWidth={2}
          aria-hidden
        />
      ),
      iconClassName: "bg-aera-redlight text-aera-red",
    },
    {
      id: "sheets",
      label: "Google Sheets",
      icon: (
        <FileSpreadsheet
          className="h-5 w-5 text-aera-green"
          strokeWidth={2}
          aria-hidden
        />
      ),
      iconClassName: "bg-aera-greenlight text-aera-green",
    },
    {
      id: "config",
      label: "วิธีขายของร้าน",
      icon: <Icon className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />,
      iconClassName: `${iconBg} ${iconColor}`,
    },
  ];
}

function GoogleSheetsSubtitle({
  status,
  workspaceId,
  isLoading,
  error,
}: {
  status: GoogleIntegrationStatus | null;
  workspaceId?: string | null;
  isLoading: boolean;
  error: string | null;
}) {
  if (isLoading) {
    return <>กำลังตรวจสอบ…</>;
  }

  if (error) {
    return <span className="text-aera-red">ตรวจสอบไม่สำเร็จ</span>;
  }

  const { label, tone } = getGoogleConnectionPresentation(status, workspaceId);
  const { textClassName } = getGoogleConnectionPresentationStyle(tone);

  return (
    <span className={`inline-block max-w-full truncate ${textClassName}`}>
      {label}
    </span>
  );
}

function CombinedTransactionSubtitle({
  summary,
}: {
  summary: CombinedTransactionSummary;
}) {
  if (summary.totalCount <= 0) {
    return <>ยังไม่มีรายการ</>;
  }

  const countLabel = `${summary.totalCount.toLocaleString("th-TH")} รายการ`;
  const { label, isNegative } =
    formatCombinedTransactionNetAmountLabel(summary);

  return (
    <>
      {countLabel} ·{" "}
      <span className={isNegative ? "text-aera-red" : "text-aera-green"}>
        {label}
      </span>
    </>
  );
}

type ShopInfoCardProps = {
  icon: React.ReactNode;
  iconClassName: string;
  title: string;
  subtitle: React.ReactNode;
  arrowType?: "external" | "chevron";
  onClick?: () => void;
};

function useMinWidthMd() {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setMatches(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return matches;
}

function ShopInfoCardsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3" aria-hidden>
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="min-w-0 overflow-hidden rounded-xl border border-aera-lightgray/80 bg-aera-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.01)]"
        >
          <div className="flex w-full min-w-0 flex-col gap-2">
            <div className="flex items-start justify-between gap-1.5">
              <div className="h-7 w-7 shrink-0 rounded-[10px] bg-aera-lightgray/60 animate-pulse" />
              <div className="h-3.5 w-3.5 shrink-0 rounded-sm bg-aera-lightgray/40 animate-pulse" />
            </div>
            <div className="min-w-0 space-y-1.5">
              <div className="h-3 w-[72%] max-w-full rounded bg-aera-lightgray/60 animate-pulse" />
              <div className="h-2.5 w-[56%] max-w-full rounded bg-aera-lightgray/50 animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ShopInfoCard({
  icon,
  iconClassName,
  title,
  subtitle,
  arrowType = "external",
  onClick,
}: ShopInfoCardProps) {
  return (
    <AeraButton
      type="button"
      variant="transparent"
      paddingSize="none"
      onClick={onClick}
      className="items-start! justify-start border border-aera-lightgray/80 bg-aera-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.01)] transition-all! duration-300! ease-[cubic-bezier(0.16,1,0.3,1)]! hover:-translate-y-0.5 hover:border-aera-amber hover:bg-aera-white! hover:shadow-sm"
    >
      <div className="flex w-full flex-col gap-2 text-left">
        <div className="flex items-start justify-between gap-1.5">
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] ${iconClassName}`}
          >
            {icon}
          </span>
          {arrowType === "chevron" ? (
            <ChevronRight
              size={14}
              className="shrink-0 text-aera-gray/70 transition-all duration-150 ease-out group-hover:translate-x-px group-hover:-translate-y-px group-hover:text-aera-charcoal"
            />
          ) : (
            <ArrowUpRight
              size={14}
              className="shrink-0 text-aera-gray/70 transition-all duration-150 ease-out group-hover:translate-x-px group-hover:-translate-y-px group-hover:text-aera-charcoal"
            />
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-bold text-aera-charcoal">
            {title}
          </p>
          <p className="mt-0.5 truncate text-xxs text-aera-gray">{subtitle}</p>
        </div>
      </div>
    </AeraButton>
  );
}

function PinManagerDialog({
  isOpen,
  pinOptions,
  draftPins,
  onDraftChange,
  onSave,
  onClose,
  isSaving = false,
}: {
  isOpen: boolean;
  pinOptions: PinPresentation[];
  draftPins: ActivePins;
  onDraftChange: (id: PinId, checked: boolean) => void;
  onSave: () => void;
  onClose: () => void;
  isSaving?: boolean;
}) {
  const isDesktop = useMinWidthMd();

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      align={isDesktop ? "center" : "bottom"}
      zIndexClass="z-[120]"
      panelClassName={[
        "flex flex-col overflow-hidden bg-aera-white shadow-xl",
        isDesktop
          ? "w-full max-w-md max-h-[90dvh] rounded-4xl"
          : "max-h-[80dvh] rounded-t-2xl border-t border-aera-lightgray",
      ].join(" ")}
    >
      {!isDesktop ? (
        <div className="flex shrink-0 justify-center pb-3 pt-2">
          <div
            className="h-1 w-10 rounded-full bg-aera-lightgray"
            aria-hidden
          />
        </div>
      ) : null}

      <header
        className={[
          "flex shrink-0 items-center justify-between gap-3 px-4 pb-3",
          isDesktop ? "pt-5" : "",
        ].join(" ")}
      >
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-aera-charcoal">
            จัดการข้อมูลร้านที่ใช้บ่อย
          </h2>
          <p className="mt-0.5 text-xs leading-relaxed text-aera-gray">
            เลือกข้อมูลสำคัญที่อยากให้แสดงบนหน้าหลัก
            เพื่อเปิดดูและตรวจงานได้เร็ว
          </p>
        </div>
        <ModalCloseButton onClose={onClose} className="-mr-1 shrink-0" />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        <div className="divide-y divide-aera-lightgray">
          {pinOptions.map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-center justify-between gap-3 py-3"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-[10px] ${option.iconClassName}`}
                >
                  {option.icon}
                </span>
                <span className="text-xs font-bold text-aera-slate">
                  {option.label}
                </span>
              </span>
              <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-aera-lightgray/80 bg-aera-white transition-colors has-checked:border-aera-amber has-checked:bg-aera-amber">
                <input
                  type="checkbox"
                  checked={draftPins[option.id]}
                  onChange={(event) =>
                    onDraftChange(option.id, event.target.checked)
                  }
                  className="peer absolute inset-0 cursor-pointer opacity-0"
                />
                <Check
                  className="pointer-events-none h-3 w-3 text-aera-charcoal opacity-0 transition-opacity peer-checked:opacity-100"
                  strokeWidth={3}
                  aria-hidden
                />
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-2 gap-2 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <AeraButton
          type="button"
          variant="primary"
          fontWeight="medium"
          className="cursor-pointer"
          onClick={onSave}
          disabled={isSaving}
        >
          บันทึกการแสดงผล
        </AeraButton>
        <AeraButton
          type="button"
          variant="outline"
          fontWeight="medium"
          className="cursor-pointer"
          onClick={onClose}
          disabled={isSaving}
        >
          ยกเลิก
        </AeraButton>
      </div>
    </ModalShell>
  );
}

type WorkerFrequentShopSectionProps = {
  workspace?: Workspace | null;
  onWorkspaceUpdated?: () => void;
  pinDetailBridgeRef?: React.MutableRefObject<WorkerShopPinDetailBridge | null>;
  isWorkspaceLoading?: boolean;
};

const RECENT_ACTIVITY_PREVIEW_LIMIT = 5;

function getActivitySortMs(iso: string | null | undefined): number {
  if (!iso?.trim()) return 0;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : 0;
}

function buildGoogleSheetsActivityItem(subtitle: string, onClick: () => void) {
  return {
    id: "google-sheets",
    icon: (
      <FileSpreadsheet
        className="h-4 w-4 text-aera-green"
        strokeWidth={2}
        aria-hidden
      />
    ),
    iconClassName: "bg-aera-greenlight text-aera-green",
    title: "Google Sheets พร้อมใช้งาน",
    subtitle,
    trailing: (
      <ChevronRight size={14} className="text-aera-gray/70" aria-hidden />
    ),
    onClick,
  };
}

export function WorkerRecentActivitySection({
  workspace = null,
}: {
  workspace?: Workspace | null;
}) {
  const router = useRouter();
  const [isShowingAllActivity, setIsShowingAllActivity] = useState(false);
  const {
    recentTransactions,
    isLoading: isRecentTransactionsLoading,
    error: recentTransactionsError,
  } = useWorkspaceRecentTransactions(workspace?.id);
  const {
    status: googleIntegrationStatus,
    isLoading: isGoogleIntegrationLoading,
    error: googleIntegrationError,
  } = useGoogleIntegrationStatus();

  useEffect(() => {
    setIsShowingAllActivity(false);
  }, [workspace?.id]);

  const isSheetsConnected = useMemo(() => {
    if (isGoogleIntegrationLoading || googleIntegrationError) return false;
    return getGoogleConnectionPresentation(
      googleIntegrationStatus,
      workspace?.id,
    ).isConnected;
  }, [
    googleIntegrationError,
    googleIntegrationStatus,
    isGoogleIntegrationLoading,
    workspace?.id,
  ]);

  const sheetsLastActivityAt = useMemo(() => {
    const latest = recentTransactions[0]?.record;
    return latest ? getTransactionRecordedAtIso(latest) : null;
  }, [recentTransactions]);

  const sheetsLastActivityLabel = useMemo(() => {
    if (isGoogleIntegrationLoading) return "กำลังตรวจสอบ…";
    if (googleIntegrationError) return "โหลดไม่สำเร็จ";
    return formatGoogleSheetsLastActivity(
      googleIntegrationStatus,
      sheetsLastActivityAt,
    );
  }, [
    googleIntegrationError,
    googleIntegrationStatus,
    isGoogleIntegrationLoading,
    sheetsLastActivityAt,
  ]);

  const sheetsActivitySubtitle = useMemo(() => {
    const { label } = getGoogleConnectionPresentation(
      googleIntegrationStatus,
      workspace?.id,
    );
    const statusLabel = label.split(" (")[0] ?? label;
    const activityLabel =
      sheetsLastActivityLabel &&
      sheetsLastActivityLabel !== "—" &&
      sheetsLastActivityLabel !== "ยังไม่มีกิจกรรม" &&
      sheetsLastActivityLabel !== "กำลังตรวจสอบ…" &&
      sheetsLastActivityLabel !== "โหลดไม่สำเร็จ"
        ? sheetsLastActivityLabel
        : "อัปเดตให้อัตโนมัติ";

    return `${statusLabel} · ${activityLabel}`;
  }, [googleIntegrationStatus, sheetsLastActivityLabel, workspace?.id]);

  const openTransactionActivity = useCallback(
    (isExpense: boolean) => {
      const workspaceId = workspace?.id?.trim();
      if (!workspaceId) {
        showToast("ไม่พบเวิร์กสเปซที่จะเปิดรายการ");
        return;
      }

      router.push(
        buildBillinDestination("app", workspaceId, {
          scrollTo: BILLIN_SCROLL_TO_TRANSACTIONS,
          workerId: isExpense
            ? EXPENSE_TRANSACTION_WORKER_ID
            : DEFAULT_TRANSACTION_WORKER_ID,
        }),
      );
    },
    [router, workspace?.id],
  );

  const openSheetsActivity = useCallback(() => {
    openGoogleSheetsFromShop({
      router,
      workspaceId: workspace?.id,
      status: googleIntegrationStatus,
    });
  }, [googleIntegrationStatus, router, workspace?.id]);

  const handleViewAllActivity = useCallback(() => {
    setIsShowingAllActivity(true);
  }, []);

  const handleCollapseActivity = useCallback(() => {
    setIsShowingAllActivity(false);
  }, []);

  const allRecentActivityItems = useMemo(() => {
    type ActivityListItem = {
      sortAtMs: number;
      id: string;
      icon: React.ReactNode;
      iconClassName: string;
      title: string;
      subtitle: string;
      trailing?: React.ReactNode;
      onClick?: () => void;
    };

    const items: ActivityListItem[] = recentTransactions.map(
      ({ record, isExpense }, index) => {
        const recordedAt = getTransactionRecordedAtIso(record);
        const datetimeLabel = recordedAt ? formatThaiDatetime(recordedAt) : "—";
        const amountLabel = formatActivityAmountLabel(record, isExpense);
        const recordId = getTransactionRecordId(record);

        return {
          sortAtMs: getActivitySortMs(recordedAt),
          id: recordId || `transaction-${index}`,
          icon: <BahtIcon className="h-4 w-4" />,
          iconClassName: "bg-aera-greenlight text-aera-green",
          title: isExpense
            ? "Bill-Lin บันทึกรายจ่ายแล้ว"
            : "Bill-Lin บันทึกรายรับแล้ว",
          subtitle: `${datetimeLabel} · รายการเงินเข้าออก`,
          trailing: amountLabel ? (
            <span
              className={`text-xs font-bold ${isExpense ? "text-aera-red" : "text-aera-green"}`}
            >
              {amountLabel}
            </span>
          ) : undefined,
          onClick: () => openTransactionActivity(isExpense),
        };
      },
    );

    if (isSheetsConnected) {
      const connectedAt = resolveGoogleConnectionTimestamp(
        googleIntegrationStatus,
      );
      items.push({
        sortAtMs: getActivitySortMs(connectedAt),
        ...buildGoogleSheetsActivityItem(
          sheetsActivitySubtitle,
          openSheetsActivity,
        ),
      });
    }

    return items
      .sort((left, right) => right.sortAtMs - left.sortAtMs)
      .slice(0, RECENT_WORKSPACE_TRANSACTION_LIMIT)
      .map(({ sortAtMs, ...item }) => {
        void sortAtMs;
        return item;
      });
  }, [
    googleIntegrationStatus,
    isSheetsConnected,
    openSheetsActivity,
    openTransactionActivity,
    recentTransactions,
    sheetsActivitySubtitle,
  ]);

  const visibleRecentActivityItems = useMemo(() => {
    if (isShowingAllActivity) {
      return allRecentActivityItems;
    }
    return allRecentActivityItems.slice(0, RECENT_ACTIVITY_PREVIEW_LIMIT);
  }, [allRecentActivityItems, isShowingAllActivity]);

  const hasExpandableActivity =
    allRecentActivityItems.length > RECENT_ACTIVITY_PREVIEW_LIMIT;
  const canShowMoreActivity = !isShowingAllActivity && hasExpandableActivity;
  const canCollapseActivity = isShowingAllActivity && hasExpandableActivity;

  const isRecentActivityLoading =
    isRecentTransactionsLoading || isGoogleIntegrationLoading;

  return (
    <>
      <section className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-bold text-aera-charcoal">
            กิจกรรมล่าสุด
          </h2>
          {canShowMoreActivity ? (
            <AeraButton
              type="button"
              variant="disabled"
              textSize="xs"
              fontWeight="medium"
              paddingSize="xxs"
              onClick={handleViewAllActivity}
              className="gap-1! border-none text-aera-amberdark hover:bg-aera-warmcream"
            >
              ดูทั้งหมด
            </AeraButton>
          ) : canCollapseActivity ? (
            <AeraButton
              type="button"
              variant="disabled"
              textSize="xs"
              fontWeight="medium"
              paddingSize="xxs"
              onClick={handleCollapseActivity}
              className="gap-1! border-none text-aera-amberdark hover:bg-aera-warmcream"
            >
              ดูแบบย่อ
            </AeraButton>
          ) : null}
        </div>

        <div className="mt-1.5 overflow-hidden rounded-[14px] border border-aera-lightgray/80 bg-aera-white shadow-[0_1px_2px_rgba(0,0,0,0.01)]">
          {isRecentActivityLoading ? (
            <div className="px-3 py-4 text-center text-[11px] text-aera-gray">
              กำลังโหลด…
            </div>
          ) : recentTransactionsError ? (
            <div className="px-3 py-4 text-center text-[11px] text-aera-gray">
              โหลดกิจกรรมไม่สำเร็จ
            </div>
          ) : visibleRecentActivityItems.length > 0 ? (
            visibleRecentActivityItems.map((item, index) => (
              <ActivityFeedItem
                key={item.id}
                icon={item.icon}
                iconClassName={item.iconClassName}
                title={item.title}
                subtitle={item.subtitle}
                trailing={item.trailing}
                onClick={item.onClick}
                isLast={index === visibleRecentActivityItems.length - 1}
              />
            ))
          ) : (
            <div className="px-3 py-4 text-center text-[11px] text-aera-gray">
              ยังไม่มีกิจกรรมล่าสุด
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function WorkerFrequentShopSection({
  workspace = null,
  onWorkspaceUpdated,
  pinDetailBridgeRef,
  isWorkspaceLoading = false,
}: WorkerFrequentShopSectionProps) {
  const router = useRouter();
  const flowPickerHostRef = useRef<HTMLDivElement>(null);
  const {
    pins: activePins,
    setPins: setActivePins,
    isLoading: isPinsLoading,
  } = useWorkspaceShopPins(workspace);
  const [draftPins, setDraftPins] = useState<ActivePins>(DEFAULT_ACTIVE_PINS);
  const [isManaging, setIsManaging] = useState(false);
  const [isFlowPickerOpen, setIsFlowPickerOpen] = useState(false);
  const [draftBusinessType, setDraftBusinessType] =
    useState<AeraBusinessType | null>(null);
  const [isSavingBusinessType, setIsSavingBusinessType] = useState(false);
  const [isSavingPins, setIsSavingPins] = useState(false);
  const isSectionLoading =
    isPinsLoading || (isWorkspaceLoading && !workspace?.id);

  useEffect(() => {
    if (!isManaging) {
      setDraftPins(activePins);
    }
  }, [activePins, isManaging]);

  const openPinManager = () => {
    setDraftPins(activePins);
    setIsManaging(true);
  };

  const closePinManager = () => {
    setIsManaging(false);
  };

  const savePinConfig = async () => {
    const workspaceId = workspace?.id?.trim();
    if (!workspaceId) {
      showToast("ไม่พบเวิร์กสเปซที่จะบันทึก");
      return;
    }

    try {
      setIsSavingPins(true);
      await saveWorkspaceShopPinsConfig(
        workspaceId,
        draftPins,
        workspace?.tags,
      );
      setActivePins(draftPins);
      closePinManager();
      onWorkspaceUpdated?.();
      showToast("บันทึกหน้าหลักแล้ว");
    } catch {
      showToast("บันทึกไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setIsSavingPins(false);
    }
  };

  const handleDraftChange = (id: PinId, checked: boolean) => {
    setDraftPins((prev) => ({ ...prev, [id]: checked }));
  };

  const openFlowPicker = useCallback(() => {
    setDraftBusinessType(resolveWorkspaceBusinessType(workspace));
    setIsFlowPickerOpen(true);
  }, [workspace]);

  const handleFlowPickerPick = (option: AeraBusinessOption) => {
    setDraftBusinessType(option.id);
  };

  const handleFlowPickerConfirm = async () => {
    if (draftBusinessType == null) {
      setIsFlowPickerOpen(false);
      return;
    }

    const workspaceId = workspace?.id?.trim();
    if (!workspaceId) {
      showToast("ไม่พบเวิร์กสเปซที่จะบันทึก");
      setIsFlowPickerOpen(false);
      return;
    }

    const currentType = resolveWorkspaceBusinessType(workspace);
    if (draftBusinessType === currentType) {
      setIsFlowPickerOpen(false);
      return;
    }

    const selectedOption = AERA_BUSINESS_OPTIONS.find(
      (option) => option.id === draftBusinessType,
    );
    if (!selectedOption) {
      showToast("ไม่พบรูปแบบธุรกิจที่เลือก");
      return;
    }

    try {
      setIsSavingBusinessType(true);
      await saveWorkspaceBusinessTypeConfig(
        workspaceId,
        selectedOption,
        workspace?.tags,
      );
      onWorkspaceUpdated?.();
      showToast("บันทึกวิธีขายแล้ว");
      setIsFlowPickerOpen(false);
    } catch {
      showToast("บันทึกไม่สำเร็จ กรุณาลองใหม่");
    } finally {
      setIsSavingBusinessType(false);
    }
  };

  const {
    summary: transactionSummary,
    isLoading: isTransactionSummaryLoading,
    error: transactionSummaryError,
  } = useWorkspaceTransactionSummary(workspace?.id);
  const {
    status: googleIntegrationStatus,
    isLoading: isGoogleIntegrationLoading,
    error: googleIntegrationError,
  } = useGoogleIntegrationStatus();
  const { cardSubtitle: receivableSubtitle } =
    useWorkspaceReceivableSummary(workspace);

  const activatePin = useCallback(
    (id: PinId) => {
      if (id === "config") {
        openFlowPicker();
        return;
      }

      if (id === "sheets") {
        openGoogleSheetsFromShop({
          router,
          workspaceId: workspace?.id,
          status: googleIntegrationStatus,
        });
        return;
      }

      const workspaceId = workspace?.id?.trim();
      if (!workspaceId) {
        showToast("ไม่พบเวิร์กสเปซที่จะเปิดรายการ");
        return;
      }

      if (id === "transactions") {
        router.push(
          buildBillinDestination("app", workspaceId, {
            scrollTo: BILLIN_SCROLL_TO_TRANSACTIONS,
          }),
        );
        return;
      }

      if (id === "receivable") {
        router.push(
          buildBillinDestination("app", workspaceId, {
            scrollTo: BILLIN_SCROLL_TO_RECEIVABLE,
            workerId: BILLIN_RECEIVABLE_WORKER_ID,
          }),
        );
      }
    },
    [googleIntegrationStatus, openFlowPicker, router, workspace?.id],
  );

  useEffect(() => {
    if (!pinDetailBridgeRef) return;
    pinDetailBridgeRef.current = { openPinDetail: activatePin };
    return () => {
      pinDetailBridgeRef.current = null;
    };
  }, [activatePin, pinDetailBridgeRef]);

  const transactionsSubtitle = useMemo(
    () =>
      isTransactionSummaryLoading ? (
        "กำลังโหลด…"
      ) : transactionSummaryError ? (
        "โหลดสรุปไม่สำเร็จ"
      ) : (
        <CombinedTransactionSubtitle summary={transactionSummary} />
      ),
    [isTransactionSummaryLoading, transactionSummary, transactionSummaryError],
  );

  const sheetsSubtitle = useMemo(
    () => (
      <GoogleSheetsSubtitle
        status={googleIntegrationStatus}
        workspaceId={workspace?.id}
        isLoading={isGoogleIntegrationLoading}
        error={googleIntegrationError}
      />
    ),
    [
      googleIntegrationError,
      googleIntegrationStatus,
      isGoogleIntegrationLoading,
      workspace?.id,
    ],
  );

  const visiblePinCount = Object.values(activePins).filter(Boolean).length;
  const salesConfigLabel = useMemo(
    () => resolveWorkspaceSalesConfigLabel(workspace),
    [workspace],
  );
  const pinPresentations = useMemo(
    () => buildPinPresentations(resolveWorkspaceBusinessType(workspace)),
    [workspace],
  );
  const salesConfigCardIcon = useMemo(() => {
    const configPin = pinPresentations.find((pin) => pin.id === "config");
    return {
      icon: configPin?.icon ?? null,
      iconClassName: configPin?.iconClassName ?? "",
    };
  }, [pinPresentations]);
  const receivableCardIcon = useMemo(() => {
    const receivablePin = pinPresentations.find(
      (pin) => pin.id === "receivable",
    );
    return {
      icon: receivablePin?.icon ?? null,
      iconClassName: receivablePin?.iconClassName ?? "",
    };
  }, [pinPresentations]);

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between px-1">
          <span className="text-sm font-bold text-aera-charcoal">
            ข้อมูลร้านที่ใช้บ่อย
          </span>
          <AeraButton
            type="button"
            variant="disabled"
            textSize="xs"
            fontWeight="medium"
            paddingSize="xxs"
            onClick={openPinManager}
            icon={<Settings size={12} />}
            disabled={isSectionLoading}
            className="gap-1! border-none text-aera-amberdark hover:bg-aera-warmcream"
          >
            จัดการ
          </AeraButton>
        </div>

        {isSectionLoading ? (
          <ShopInfoCardsSkeleton count={Math.max(visiblePinCount, 2)} />
        ) : visiblePinCount > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {activePins.transactions ? (
              <ShopInfoCard
                icon={<BahtIcon className="h-5 w-5 text-aera-green" />}
                iconClassName="bg-aera-greenlight text-aera-green"
                title="รายการเงินเข้า/ออก"
                subtitle={transactionsSubtitle}
                onClick={() => activatePin("transactions")}
              />
            ) : null}
            {activePins.receivable ? (
              <ShopInfoCard
                icon={receivableCardIcon.icon}
                iconClassName={receivableCardIcon.iconClassName}
                title="ยอดค้าง"
                subtitle={receivableSubtitle}
                onClick={() => activatePin("receivable")}
              />
            ) : null}
            {activePins.sheets ? (
              <ShopInfoCard
                icon={
                  <FileSpreadsheet
                    className="h-5 w-5 text-aera-green"
                    strokeWidth={2}
                    aria-hidden
                  />
                }
                iconClassName="bg-aera-greenlight text-aera-green"
                title="Google Sheets"
                subtitle={sheetsSubtitle}
                onClick={() => activatePin("sheets")}
              />
            ) : null}
            {activePins.config ? (
              <ShopInfoCard
                icon={salesConfigCardIcon.icon}
                iconClassName={salesConfigCardIcon.iconClassName}
                title="วิธีขายของร้าน"
                subtitle={salesConfigLabel}
                arrowType="chevron"
                onClick={() => activatePin("config")}
              />
            ) : null}
          </div>
        ) : (
          <AeraButton
            type="button"
            variant="transparent"
            fullWidth
            textSize="xs"
            paddingSize="none"
            onClick={openPinManager}
            className="rounded-xl border border-dashed border-aera-lightgray bg-aera-offwhite px-3 py-4 text-center text-aera-gray hover:border-aera-amber hover:bg-aera-offwhite! hover:text-aera-charcoal"
          >
            ยังไม่ได้เลือกข้อมูลที่แสดง — กดจัดการเพื่อเลือก
          </AeraButton>
        )}

        <div className="rounded-lg border-l-4 border-l-aera-amber bg-aera-offwhite px-3 py-2.5">
          <p className="text-xs leading-relaxed text-aera-gray">
            📌 เลือกแสดงเฉพาะข้อมูลที่ต้องเปิดบ่อย เช่น รายการเงิน ยอดค้าง
            และตั้งค่าร้าน
          </p>
        </div>
      </div>

      <PinManagerDialog
        isOpen={isManaging}
        pinOptions={pinPresentations}
        draftPins={draftPins}
        onDraftChange={handleDraftChange}
        onSave={() => void savePinConfig()}
        onClose={closePinManager}
        isSaving={isSavingPins}
      />

      <div ref={flowPickerHostRef} className="hidden" aria-hidden />
      <FlowPickerSheet
        isOpen={isFlowPickerOpen}
        onClose={() => {
          if (isSavingBusinessType) return;
          setIsFlowPickerOpen(false);
        }}
        onConfirm={handleFlowPickerConfirm}
        confirmLoading={isSavingBusinessType}
        options={AERA_BUSINESS_OPTIONS}
        selectedId={draftBusinessType}
        onPick={handleFlowPickerPick}
        hostRef={flowPickerHostRef}
      />
    </>
  );
}
